/**
 * ResumeReferralsPage — V1.5.8.1 stats hub at /resume/referrals?email=X.
 *
 * Shows the referrer's:
 *   - Code + share link
 *   - Progress (paid_conversion_count / goal_conversions)
 *   - Wallet balance & lifetime earned
 *   - Friend list (masked emails, status pending/paid, timestamp)
 *
 * Auth model: caller provides their email via query param OR the "look up my
 * stats" input on the page. No password gate — the friend list is masked,
 * so exposure risk is low. Anti-scraping is by rate-limit on the shared
 * /referrals/mine endpoint.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Brand from "@/components/Brand";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
    Copy, WhatsappLogo, EnvelopeSimple, Gift, CheckCircle,
    ArrowLeft, Users, Wallet, WarningCircle,
} from "@phosphor-icons/react";

const rupees = (paise) => `₹${Math.floor((paise || 0) / 100)}`;

function StatusChip({ status, suspicious }) {
    if (suspicious) {
        return (
            <span data-testid="rs-ref-status-suspicious"
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                <WarningCircle size={11} weight="bold" /> Flagged
            </span>
        );
    }
    if (status === "paid") {
        return (
            <span data-testid="rs-ref-status-paid"
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                <CheckCircle size={11} weight="bold" /> Paid
            </span>
        );
    }
    return (
        <span data-testid="rs-ref-status-pending"
              className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-700">
            Pending
        </span>
    );
}

export default function ResumeReferralsPage() {
    const [params, setParams] = useSearchParams();
    const [email, setEmail] = useState(() => params.get("email") || "");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);   // { stats, events }
    const [error, setError] = useState("");
    const [inputEmail, setInputEmail] = useState("");
    const [copied, setCopied] = useState(false);
    // V1.5.8.2 — OTP gate state.
    const [authed, setAuthed] = useState(false);
    const [otpStage, setOtpStage] = useState("idle"); // idle | requesting | sent | verifying | verified
    const [otpEmail, setOtpEmail] = useState("");
    const [otpCode, setOtpCode] = useState("");
    const [otpError, setOtpError] = useState("");

    const load = async (em) => {
        if (!em || !em.includes("@")) return;
        setLoading(true);
        setError("");
        try {
            // Cookie-first: if the user has an rs_ref_token cookie, this call
            // returns { authed: true } and we don't need to send ?email=.
            const primary = await fetch(`${API}/resume-studio/referrals/history`, {
                credentials: "include",
            });
            let hist = await primary.json();
            if (primary.ok && hist.authed) {
                setData(hist);
                setAuthed(true);
                if (!hist.stats) {
                    // First-time authed visitor — mint their code so the hub is populated.
                    const mint = await fetch(`${API}/resume-studio/referrals/mine?email=${encodeURIComponent(em)}`);
                    const mintBody = await mint.json();
                    if (mint.ok) setData({ stats: mintBody.stats, events: [] });
                }
                return;
            }
            // Fallback (unauthed) — use the query param path but require the OTP gate to unlock the hub.
            const q = await fetch(`${API}/resume-studio/referrals/history?email=${encodeURIComponent(em)}`);
            hist = await q.json();
            if (!q.ok) throw new Error(hist.detail || "Couldn't load referral history");
            if (!hist.stats) {
                const mint = await fetch(`${API}/resume-studio/referrals/mine?email=${encodeURIComponent(em)}`);
                const mintBody = await mint.json();
                if (!mint.ok) throw new Error(mintBody.detail || "Couldn't mint code");
                setData({ stats: mintBody.stats, events: [] });
            } else {
                setData(hist);
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Try the cookie-auth path unconditionally on mount — if the user
        // already OTP'd in the last 24h their hub loads immediately.
        (async () => {
            try {
                const r = await fetch(`${API}/resume-studio/referrals/history`, {
                    credentials: "include",
                });
                const body = await r.json();
                if (r.ok && body.authed) {
                    setAuthed(true);
                    // If stats is null (never minted before), mint it now — the
                    // authed cookie's `sub` claim is the trusted email.
                    setData(body);
                }
            } catch { /* soft-fail */ }
        })();
    }, []);

    useEffect(() => {
        // Only re-fetch when we have BOTH an email AND we're authed (or
        // following a legacy ?email= link).
        if (email && (authed || params.get("email"))) load(email);
    }, [email, authed]);

    const onLookup = (e) => {
        e.preventDefault();
        if (!inputEmail || !inputEmail.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        setEmail(inputEmail.trim().toLowerCase());
        setParams({ email: inputEmail.trim().toLowerCase() }, { replace: true });
    };

    // V1.5.8.2 — OTP gate actions.
    const requestOtp = async () => {
        if (!otpEmail || !otpEmail.includes("@")) {
            setOtpError("Enter a valid email");
            return;
        }
        setOtpStage("requesting");
        setOtpError("");
        try {
            const r = await fetch(`${API}/resume-studio/referrals/otp/request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: otpEmail.trim().toLowerCase() }),
            });
            if (!r.ok) throw new Error("Couldn't send code");
            setOtpStage("sent");
            toast.success("6-digit code sent — check your inbox.");
        } catch (e) {
            setOtpError(e.message);
            setOtpStage("idle");
        }
    };
    const verifyOtp = async () => {
        if (!/^\d{6}$/.test(otpCode)) {
            setOtpError("Enter the 6-digit code from your email");
            return;
        }
        setOtpStage("verifying");
        setOtpError("");
        try {
            const r = await fetch(`${API}/resume-studio/referrals/otp/verify`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: otpEmail.trim().toLowerCase(),
                    otp: otpCode,
                }),
            });
            const body = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(body.detail || "Invalid code");
            setAuthed(true);
            setOtpStage("verified");
            setEmail(otpEmail.trim().toLowerCase());
            setParams({ email: otpEmail.trim().toLowerCase() }, { replace: true });
            toast.success("Signed in to your referral hub.");
        } catch (e) {
            setOtpError(e.message);
            setOtpStage("sent"); // let them retry
        }
    };
    const logout = async () => {
        try {
            await fetch(`${API}/resume-studio/referrals/logout`, {
                method: "POST", credentials: "include",
            });
        } catch { /* soft-fail */ }
        setAuthed(false);
        setData(null);
        setEmail("");
        setOtpStage("idle");
        setOtpEmail("");
        setOtpCode("");
        setParams({}, { replace: true });
        toast.success("Signed out.");
    };

    const stats = data?.stats;
    const events = data?.events || [];
    const shareUrl = stats ? `${window.location.origin}/resume?ref=${stats.code}` : "";
    const paid = stats?.paid_conversion_count || 0;
    const goal = stats?.goal_conversions || 5;
    const pct = stats ? Math.min(100, Math.round((paid / goal) * 100)) : 0;

    const copy = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link copied — share it with a friend!");
            setTimeout(() => setCopied(false), 2000);
        } catch { toast.error("Couldn't copy"); }
    };
    const shareVia = (channel) => {
        const msg = `I got my CV rewritten by AI on NaukriDaddy — genuinely worth it. Here's a free AI CV check on me: ${shareUrl}`;
        if (channel === "whatsapp") {
            window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
        } else if (channel === "email") {
            window.location.href = `mailto:?subject=${encodeURIComponent("Free AI CV check on NaukriDaddy 🎁")}&body=${encodeURIComponent(msg)}`;
        }
    };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="rs-referrals-page">
            <header className="border-b border-slate-200 bg-white">
                <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
                    <Brand />
                    <div className="flex items-center gap-3">
                        {authed && (
                            <button
                                type="button"
                                onClick={logout}
                                data-testid="rs-referrals-logout-btn"
                                className="text-xs font-black uppercase tracking-wider text-slate-500 hover:text-red-600"
                            >
                                Sign out
                            </button>
                        )}
                        <Link
                            to="/resume"
                            data-testid="rs-referrals-back-link"
                            className="text-xs font-black uppercase tracking-wider text-slate-600 hover:text-cobalt inline-flex items-center gap-1"
                        >
                            <ArrowLeft size={12} weight="bold" /> Back to Resume Studio
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
                <div className="mb-8">
                    <p className="text-[10px] font-black uppercase tracking-widest text-cobalt mb-2">
                        Referrals · Resume Studio
                    </p>
                    <h1 className="text-3xl md:text-5xl font-black tracking-tight text-slate-900">
                        Your referral bonus. <span className="text-cobalt">Live.</span>
                    </h1>
                    <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl">
                        ₹10 credit each time a friend you referred pays for any tier. Caps at ₹49 lifetime.
                        Redeems automatically at your next Resume Studio checkout.
                    </p>
                </div>

                {/* V1.5.8.2 — OTP-gated sign-in. If they haven't authed yet, show the OTP flow.
                    The email query fallback is kept as a legacy path via the "See without signing in" link. */}
                {!authed && !data && (
                    <div className="rounded-lg border border-slate-200 bg-white p-5 md:p-6 mb-6" data-testid="rs-referrals-otp-card">
                        <h2 className="text-lg font-black tracking-tight text-slate-900 mb-1">Sign in to your referral hub</h2>
                        <p className="text-xs text-slate-600 mb-4">
                            We'll email a 6-digit code to verify it's really you. Codes expire in 15 minutes.
                        </p>
                        {otpStage === "idle" || otpStage === "requesting" ? (
                            <form onSubmit={(e) => { e.preventDefault(); requestOtp(); }}
                                  data-testid="rs-referrals-otp-request-form">
                                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-700 mb-1">
                                    Email you used at checkout
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="email"
                                        required
                                        value={otpEmail}
                                        onChange={(e) => setOtpEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        data-testid="rs-referrals-otp-email-input"
                                        className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-cobalt focus:ring-1 focus:ring-cobalt outline-none"
                                    />
                                    <button
                                        type="submit"
                                        disabled={otpStage === "requesting"}
                                        data-testid="rs-referrals-otp-request-btn"
                                        className="bg-cobalt hover:bg-cobalt-700 disabled:bg-slate-300 text-white px-4 py-2 text-xs font-black uppercase tracking-wider rounded-md"
                                    >
                                        {otpStage === "requesting" ? "Sending…" : "Send code"}
                                    </button>
                                </div>
                                {otpError && <p className="text-xs text-red-600 mt-2" data-testid="rs-referrals-otp-error">{otpError}</p>}
                            </form>
                        ) : (
                            <form onSubmit={(e) => { e.preventDefault(); verifyOtp(); }}
                                  data-testid="rs-referrals-otp-verify-form">
                                <p className="text-xs text-slate-600 mb-3">
                                    Code sent to <b data-testid="rs-referrals-otp-sent-to">{otpEmail}</b>. It'll arrive in a minute or two.
                                </p>
                                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-700 mb-1">
                                    6-digit code
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="\d{6}"
                                        maxLength={6}
                                        required
                                        value={otpCode}
                                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        placeholder="••••••"
                                        data-testid="rs-referrals-otp-code-input"
                                        className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-lg font-mono tracking-widest text-center focus:border-cobalt focus:ring-1 focus:ring-cobalt outline-none"
                                    />
                                    <button
                                        type="submit"
                                        disabled={otpStage === "verifying" || otpCode.length !== 6}
                                        data-testid="rs-referrals-otp-verify-btn"
                                        className="bg-cobalt hover:bg-cobalt-700 disabled:bg-slate-300 text-white px-4 py-2 text-xs font-black uppercase tracking-wider rounded-md"
                                    >
                                        {otpStage === "verifying" ? "Verifying…" : "Sign in"}
                                    </button>
                                </div>
                                {otpError && <p className="text-xs text-red-600 mt-2" data-testid="rs-referrals-otp-verify-error">{otpError}</p>}
                                <button
                                    type="button"
                                    onClick={() => { setOtpStage("idle"); setOtpCode(""); setOtpError(""); }}
                                    className="text-[11px] font-black uppercase tracking-wider text-slate-500 hover:text-cobalt mt-3"
                                    data-testid="rs-referrals-otp-resend-btn"
                                >
                                    ← Use a different email or resend
                                </button>
                            </form>
                        )}
                    </div>
                )}

                {/* Legacy: if the user is following an older ?email= link and we couldn't cookie-auth, drop the OTP gate and use the query param. */}
                {(!authed && email && error) && (
                    <form onSubmit={onLookup} className="rounded-lg border border-slate-200 bg-white p-5 mb-6"
                          data-testid="rs-referrals-lookup-form">
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-700 mb-2">
                            Or look up an existing hub by email (read-only)
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="email"
                                required
                                value={inputEmail}
                                onChange={(e) => setInputEmail(e.target.value)}
                                placeholder="you@example.com"
                                data-testid="rs-referrals-email-input"
                                className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-cobalt focus:ring-1 focus:ring-cobalt outline-none"
                            />
                            <button
                                type="submit"
                                data-testid="rs-referrals-lookup-submit"
                                className="bg-slate-200 hover:bg-slate-300 text-slate-800 px-4 py-2 text-xs font-black uppercase tracking-wider rounded-md"
                            >
                                Look up
                            </button>
                        </div>
                        {error && <p className="text-xs text-red-600 mt-2" data-testid="rs-referrals-lookup-error">{error}</p>}
                    </form>
                )}

                {loading && (
                    <div data-testid="rs-referrals-loading"
                         className="rounded-lg border border-slate-200 bg-white p-8 animate-pulse">
                        <div className="h-4 w-40 bg-slate-200 rounded mb-3" />
                        <div className="h-3 w-full bg-slate-100 rounded" />
                    </div>
                )}

                {stats && !loading && (
                    <>
                        {/* Progress + share strip */}
                        <div className="rounded-lg border-2 border-cobalt/30 bg-gradient-to-br from-cobalt/5 to-teal-50 p-5 md:p-6 mb-6"
                             data-testid="rs-referrals-summary-card">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="shrink-0 rounded-full bg-cobalt/10 p-2">
                                    <Gift size={22} weight="duotone" className="text-cobalt" />
                                </div>
                                <div>
                                    <p className="text-[11px] font-black uppercase tracking-wider text-slate-500">Your code</p>
                                    <h2 className="text-2xl font-black tracking-tight text-slate-900" data-testid="rs-referrals-code">
                                        {stats.code}
                                    </h2>
                                </div>
                            </div>
                            <div className="mb-4">
                                <div className="flex items-baseline justify-between text-xs mb-1.5">
                                    <span className="font-bold text-slate-800">
                                        {paid} of {goal} friends paid
                                    </span>
                                    <span className="text-slate-500">
                                        Earned {rupees(stats.credit_earned_paise)} · Balance {rupees(stats.credit_balance_paise)}
                                    </span>
                                </div>
                                <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                                    <div
                                        data-testid="rs-referrals-progress-bar"
                                        className="h-full bg-gradient-to-r from-cobalt to-teal-500 transition-all"
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                                {stats.credit_earned_paise >= stats.lifetime_max_credit_paise && (
                                    <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700">
                                        <CheckCircle size={12} weight="fill" /> Max ₹49 earned
                                    </div>
                                )}
                            </div>

                            <div className="rounded-md bg-white border border-slate-200 p-3">
                                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">
                                    Your share link
                                </p>
                                <code data-testid="rs-referrals-share-url" className="text-xs md:text-sm text-slate-800 font-mono break-all">
                                    {shareUrl}
                                </code>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-3">
                                <button type="button" onClick={copy}
                                        data-testid="rs-referrals-copy-btn"
                                        className="border border-slate-300 hover:border-cobalt hover:bg-cobalt/5 text-slate-800 px-3 py-2 text-xs font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5 rounded-md">
                                    <Copy size={14} weight="bold" /> {copied ? "Copied" : "Copy"}
                                </button>
                                <button type="button" onClick={() => shareVia("whatsapp")}
                                        data-testid="rs-referrals-whatsapp-btn"
                                        className="border border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 px-3 py-2 text-xs font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5 rounded-md">
                                    <WhatsappLogo size={14} weight="bold" /> WhatsApp
                                </button>
                                <button type="button" onClick={() => shareVia("email")}
                                        data-testid="rs-referrals-email-btn"
                                        className="border border-slate-300 hover:border-cobalt hover:bg-cobalt/5 text-slate-800 px-3 py-2 text-xs font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5 rounded-md">
                                    <EnvelopeSimple size={14} weight="bold" /> Email
                                </button>
                            </div>
                        </div>

                        {/* Wallet card */}
                        <div className="grid md:grid-cols-2 gap-4 mb-6">
                            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="rs-referrals-wallet-card">
                                <div className="flex items-center gap-2 mb-2">
                                    <Wallet size={18} weight="duotone" className="text-cobalt" />
                                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Wallet balance</p>
                                </div>
                                <p className="text-3xl font-black tracking-tight text-slate-900" data-testid="rs-referrals-balance">
                                    {rupees(stats.credit_balance_paise)}
                                </p>
                                <p className="text-[11px] text-slate-500 mt-1">
                                    Auto-applies at your next checkout (₹1 minimum payable).
                                </p>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="rs-referrals-count-card">
                                <div className="flex items-center gap-2 mb-2">
                                    <Users size={18} weight="duotone" className="text-cobalt" />
                                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Friends paid</p>
                                </div>
                                <p className="text-3xl font-black tracking-tight text-slate-900">
                                    {paid}<span className="text-slate-400 text-lg">/{goal}</span>
                                </p>
                                <p className="text-[11px] text-slate-500 mt-1">
                                    {paid >= goal ? "Goal complete — ₹49 unlocked." : `${goal - paid} more to unlock max ₹49.`}
                                </p>
                            </div>
                        </div>

                        {/* Friend list */}
                        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="rs-referrals-events-table">
                            <div className="px-5 py-3 border-b border-slate-200 flex items-baseline justify-between">
                                <h3 className="text-base font-black tracking-tight text-slate-900">Referral history</h3>
                                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                    {events.length === 0 ? "No referrals yet" : `${events.length} record${events.length === 1 ? "" : "s"}`}
                                </span>
                            </div>
                            {events.length === 0 ? (
                                <div className="px-5 py-10 text-center" data-testid="rs-referrals-empty">
                                    <p className="text-sm text-slate-500">
                                        Share your link above — every friend who lands via it appears here.
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-100">
                                    {events.map((e) => (
                                        <div key={e.id} className="px-5 py-3 flex items-center justify-between gap-3"
                                             data-testid={`rs-referrals-event-row-${e.id}`}>
                                            <div className="min-w-0">
                                                <p className="text-sm font-bold text-slate-800 truncate">
                                                    {e.referred_email_masked}
                                                </p>
                                                <p className="text-[11px] text-slate-500 mt-0.5">
                                                    Attached {e.attached_at ? new Date(e.attached_at).toLocaleDateString() : "—"}
                                                    {e.converted_at ? ` · Paid ${new Date(e.converted_at).toLocaleDateString()}` : ""}
                                                </p>
                                            </div>
                                            <div className="shrink-0 flex items-center gap-3">
                                                {e.credit_awarded_paise > 0 && (
                                                    <span className="text-sm font-black text-emerald-700">
                                                        +{rupees(e.credit_awarded_paise)}
                                                    </span>
                                                )}
                                                <StatusChip status={e.status} suspicious={e.suspicious} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
